"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ProductImageFullscreen from "./product-image-full-screen";
import useEmblaCarousel from "embla-carousel-react";
import useIsMobile from "../useIsMobile";

interface ImageThumbnailProps {
  index: number;
  url: string;
  onClick?: () => void;
  selected?: boolean;
  imagesAlt?: string[];
}

const ImageThumbnail = ({ index, url, onClick, selected, imagesAlt }: ImageThumbnailProps) => {
  const thumbnailRef = useRef<HTMLDivElement>(null);

  // When this thumbnail is selected, scroll it into view.
  useEffect(() => {
    if (selected && thumbnailRef.current) {
      thumbnailRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selected]);

  const baseUrl = url.substring(0, url.lastIndexOf("."));
  const resizedUrl = url.replace(".webp", "_resize.webp");

  return (
    <div
      ref={thumbnailRef}
      className={cn("bg-grayc4 rounded-xl p-2 h-full aspect-square", selected && "border-2 border-c2")}
    >
      <picture>
        <source srcSet={`${baseUrl}_resize.avif`} type="image/avif" />
        <source srcSet={`${baseUrl}_resize.webp`} type="image/webp" />
        <img
          src={resizedUrl}
          width={80}
          height={80}
          alt={imagesAlt ? imagesAlt[index] : `product image ${index + 1}`}
          className="w-full h-full object-contain hover:cursor-pointer"
          onClick={onClick}
          loading="lazy"
        />
      </picture>
    </div>
  );
};

interface Props {
  children?: React.ReactNode;
  imagesUrl: string[];
  currentImageUrl?: string | null;
  setSelectedImage: (image: string | null) => void;
  imagesAlt?: string[];
  imageAlt?: string;
}

const ProductImageGallery = ({
  children,
  imagesUrl,
  currentImageUrl,
  setSelectedImage,
  imagesAlt,
  imageAlt,
}: Props) => {
  const initialIndex = imagesUrl.findIndex((i) => i === currentImageUrl);
  const [selectedIndex, setSelectedIndex] = useState<number>(initialIndex < 0 ? 0 : initialIndex);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });
  const isMobile = useIsMobile();

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on("select", onSelectHandler);
    }
  }, [emblaApi]);

  const onSelectHandler = useCallback(() => {
    if (emblaApi) {
      const index = emblaApi.selectedScrollSnap();
      setSelectedIndex(index);
    }
  }, [emblaApi]);

  useEffect(() => {
    const newIndex = imagesUrl.findIndex((i) => i === currentImageUrl);
    const validIndex = newIndex >= 0 ? newIndex : 0;
    setSelectedIndex(validIndex);
    if (emblaApi) emblaApi.scrollTo(validIndex);
  }, [currentImageUrl, imagesUrl, emblaApi]);

  const handleSelectedThumbnail = (index: number) => {
    setSelectedIndex(index);
    if (emblaApi) emblaApi.scrollTo(index);
  };

  const scrollNext = () => {
    if (emblaApi && selectedIndex < imagesUrl.length - 1) {
      const newIndex = selectedIndex + 1;
      emblaApi.scrollTo(newIndex);
      setSelectedIndex(newIndex);
    }
  };

  const scrollPrev = () => {
    if (emblaApi && selectedIndex > 0) {
      const newIndex = selectedIndex - 1;
      emblaApi.scrollTo(newIndex);
      setSelectedIndex(newIndex);
    }
  };

  if (!imagesUrl || imagesUrl.length === 0) {
    return <div>No Image</div>;
  }

  return (
    <div className="flex gap-5 w-full">
      {!isMobile && (
        <div className="w-24 xl:flex hidden max-h-[570px] h-full flex-shrink-0 overflow-y-auto scrollbar-hide">
          <div className="flex flex-col gap-4">
            {imagesUrl.map((url, index) => (
              <div key={index}>
                <ImageThumbnail
                  index={index}
                  url={url}
                  onClick={() => handleSelectedThumbnail(index)}
                  selected={index === selectedIndex}
                  imagesAlt={imagesAlt}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-col w-full">
        <div className="relative">
          <div className="embla" ref={emblaRef}>
            <div className="embla__container">
              {imagesUrl.map((url, index) => (
                <div
                  key={index}
                  className="embla__slide bg-grayc4 md:rounded-xl rounded-none aspect-square"
                >
                  <ProductImageFullscreen
                    className="w-full h-full object-contain"
                    imageUrl={url}
                    priority={index === 0}
                    imageAlt={imagesAlt ? imagesAlt[index] : imageAlt}
                    setSelectedImage={setSelectedImage}
                  />
                </div>
              ))}
            </div>
            <div className="absolute bottom-5 right-5 flex gap-4">
              <button
                aria-label="Previous"
                className="embla__button embla__button--prev flex items-center justify-center rounded-full w-8 h-8 text-c4 bg-white disabled:text-grayc3"
                onClick={scrollPrev}
                disabled={selectedIndex === 0}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                aria-label="Next"
                className="embla__button embla__button--next flex items-center justify-center rounded-full w-8 h-8 text-c4 bg-white disabled:text-grayc3"
                onClick={scrollNext}
                disabled={selectedIndex === imagesUrl.length - 1}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};

export default ProductImageGallery;
